import React from 'react'
import styled from 'styled-components'
import { Search } from 'lucide-react'
import { SPACING, FONT_WEIGHT } from '../../styles/theme'

export type ResearchTab = 'all' | 'survey' | 'interview' | 'uploaded'

interface TabDef {
  key: ResearchTab
  label: string
  count: number
}

interface ResearchToolbarProps {
  tab: ResearchTab
  onTabChange: (tab: ResearchTab) => void
  counts: Record<ResearchTab, number>
  searchQuery: string
  onSearchChange: (value: string) => void
}

export const ResearchToolbar: React.FC<ResearchToolbarProps> = ({
  tab,
  onTabChange,
  counts,
  searchQuery,
  onSearchChange,
}) => {
  const tabs: TabDef[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'survey', label: 'Surveys', count: counts.survey },
    { key: 'interview', label: 'Interviews', count: counts.interview },
    { key: 'uploaded', label: 'Uploaded', count: counts.uploaded },
  ]

  return (
    <Toolbar>
      <TabGroup role="tablist">
        {tabs.map((t) => {
          const active = tab === t.key
          return (
            <Tab
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              $active={active}
              onClick={() => onTabChange(t.key)}
            >
              <TabLabel>{t.label}</TabLabel>
              <TabCount $active={active}>{t.count}</TabCount>
            </Tab>
          )
        })}
      </TabGroup>
      <SearchWrapper>
        <SearchIcon>
          <Search size={14} />
        </SearchIcon>
        <SearchInput
          type="search"
          placeholder="Search research…"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label="Search research"
        />
      </SearchWrapper>
    </Toolbar>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md};
  margin-bottom: ${SPACING.lg};
  flex-wrap: wrap;
`

const TabGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border-radius: 10px;
  background: rgba(127, 127, 127, 0.08);
`

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? 'var(--color-bg-elevated)' : 'transparent')};
  color: ${({ $active }) =>
    $active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? FONT_WEIGHT.semibold : FONT_WEIGHT.medium)};
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? 'var(--shadow-card)' : 'none')};
  transition:
    background 0.12s ease,
    color 0.12s ease;

  &:hover {
    color: var(--color-text-primary);
    background: ${({ $active }) =>
      $active ? 'var(--color-bg-elevated)' : 'rgba(127, 127, 127, 0.12)'};
  }
`

const TabLabel = styled.span``

const TabCount = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: ${FONT_WEIGHT.semibold};
  font-variant-numeric: tabular-nums;
  background: ${({ $active }) =>
    $active ? 'var(--color-primary-bg)' : 'rgba(127, 127, 127, 0.18)'};
  color: ${({ $active }) => ($active ? 'var(--color-primary)' : 'var(--color-text-tertiary)')};
`

const SearchWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 0 1 320px;
  min-width: 200px;
  height: 36px;
`

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  display: inline-flex;
  color: var(--color-text-tertiary);
  pointer-events: none;
`

const SearchInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 0 14px 0 34px;
  border-radius: 10px;
  border: 1px solid var(--color-border-dark);
  background: var(--color-bg-elevated);
  color: var(--color-text-primary);
  font-size: 13px;
  outline: none;
  transition:
    border-color 0.12s ease,
    box-shadow 0.12s ease;

  &::placeholder {
    color: var(--color-text-tertiary);
  }

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }

  /* Hide the default clear button in WebKit so it doesn't clash with our look. */
  &::-webkit-search-cancel-button {
    appearance: none;
  }
`
